<template>
  <NotFound v-if="notFound" />
  <vl-region v-if="loading">
    <div v-vl-align:center>
      <vl-loader :message="$t('general.pageLoading')" />
    </div>
  </vl-region>
  <vl-region v-if="product && !loading">
    <vl-layout>
      <PbsGoBackLink />
      <vl-grid v-vl-spacer:bottom.medium>
        <vl-column width="9" width-xs="12">
          <vl-alert
            v-if="alert.visible"
            :icon="alert.icon"
            :mod-error="alert.isError"
            :mod-success="alert.isSuccess"
            closable
            :close-text="$t('product.detail.alert.closeText')"
            :title="alert.title"
            :content="alert.content"
            @close="closeAlert"
          />
        </vl-column>
        <vl-column width="9" width-xs="12">
          <vl-title tag-name="h1">{{ $t('product.detail.title') }}</vl-title>
        </vl-column>
        <vl-column width="3" width-xs="12">
          <vl-button v-if="product.canAbort" type="button" mod-secondary @click="showcancelProductModal">{{
            $t('product.detail.cancelProduct.label')
          }}</vl-button>
        </vl-column>
        <vl-column>
          <vl-properties>
            <vl-properties-column>
              <vl-properties-list>
                <vl-properties-label>{{ $t('product.detail.definition.label') }}</vl-properties-label>
                <vl-properties-data>
                  <PbsEntityRouterLink
                    type="productProcessDefinition"
                    :detail="product.specification.definition"
                    target="_blank"
                /></vl-properties-data>
                <vl-properties-label>{{ $t('product.detail.status.label') }}</vl-properties-label>
                <vl-properties-data>{{ product.status }}</vl-properties-data>
                <vl-properties-label>{{ $t('product.detail.step.label') }}</vl-properties-label>
                <vl-properties-data>{{ product.statusId !== 100 ? product.step : "/" }}</vl-properties-data>
                <vl-properties-label v-if="product.statusId == 2 || product.statusId == 3">{{ $t('product.detail.reason.label') }}</vl-properties-label>
                <vl-properties-data v-if="product.statusId == 2 || product.statusId == 3">{{ getFirstErrorLine(product.failReason) }}</vl-properties-data> 
                <vl-properties-label>{{ $t('product.detail.isFirstTime.label') }}</vl-properties-label>
                <vl-properties-data>{{
                  product.isFirstTime ? $t('general.yes') : $t('general.no')
                }}</vl-properties-data>
                <vl-properties-label>{{ $t('product.detail.isDryRun.label') }}</vl-properties-label>
                <vl-properties-data>{{ product.isDryRun ? $t('general.yes') : $t('general.no') }}</vl-properties-data>
              </vl-properties-list>
            </vl-properties-column>
          </vl-properties>
        </vl-column>
      </vl-grid>
      <vl-grid>
        <vl-column>
          <vl-properties>
            <vl-properties-column v-if="product.runSteps && product.runSteps.length" mod-full>
              <vl-title tag-name="h2">{{ $t('product.detail.runSteps.title') }}</vl-title>
              <vl-accordion-list v-if="product.runSteps && product.runSteps.length">
                <vl-accordion-list-item v-for="(runStep, i) in product.runSteps" :id="i" :key="i">
                  <vl-accordion :id="i.toString()" :title="getRunStepDescription(runStep)" :open="!!runStep.warnings || !!runStep.error || !runStep.end">
                    <vl-properties-list v-vl-spacer:bottom.small>
                      <vl-properties-label>{{ $t('product.detail.runSteps.start.label') }}</vl-properties-label>
                      <vl-properties-data>{{ dateTimeFilter(runStep.start) }}</vl-properties-data>
                      <vl-properties-label>{{ $t('product.detail.runSteps.end.label') }}</vl-properties-label>
                      <vl-properties-data>{{ dateTimeFilter(runStep.end) }}</vl-properties-data>
                      <vl-properties-label>{{ $t('product.detail.runSteps.warnings.label') }}</vl-properties-label>
                      <vl-properties-data>
                        <div style="white-space: pre-line;">{{ runStep.warnings }}</div>
                      </vl-properties-data>
                      <vl-properties-label>{{ $t('product.detail.runSteps.error.label') }}</vl-properties-label>
                      <vl-properties-data>{{ runStep.error }}</vl-properties-data>
                    </vl-properties-list>
                  </vl-accordion>
                </vl-accordion-list-item>
              </vl-accordion-list>
            </vl-properties-column>
          </vl-properties>
        </vl-column>
      </vl-grid>
      <vl-grid v-if="!product.isDryRun" v-vl-spacer:bottom.medium>
        <vl-column>
          <vl-properties>
            <vl-properties-column mod-full>
              <vl-title tag-name="h3"> {{ $t('product.detail.metadata.title') }} </vl-title>
              <vl-annotation v-if="!product.metadata">{{ getMetadataAnnotation(product.statusId) }}</vl-annotation>
              <vl-properties-list v-if="product.metadata">
                <vl-properties-label>{{ $t('product.detail.metadata.uuid.label') }}</vl-properties-label>
                <vl-properties-data
                  ><a :href="getMetadataUrl(product.metadata.uuid)" class="vl-link" target="_blank">
                    {{ product.metadata && product.metadata.uuid }}
                  </a></vl-properties-data
                >
                <vl-properties-label>{{ $t('product.detail.metadata.versionDate.label') }}</vl-properties-label>
                <vl-properties-data>{{ dateFilter(product.metadata?.versionDate) }}</vl-properties-data>
                <vl-properties-label>{{ $t('product.detail.metadata.temporalLimitation.label') }}</vl-properties-label>
                <vl-properties-data>
                  {{ dateFilter(product.metadata?.temporalLimitationFrom) }} -
                  {{
                    dateFilter(product.metadata?.temporalLimitationTo, $t('product.detail.noEndDate.label'))
                  }}</vl-properties-data
                >
                <vl-properties-label>{{ $t('product.detail.metadata.versionDescription.label') }}</vl-properties-label>
                <vl-properties-data>{{ product.metadata && product.metadata.versionDescription }}</vl-properties-data>
              </vl-properties-list>
            </vl-properties-column>
          </vl-properties>
        </vl-column>
      </vl-grid>
      <vl-grid v-if="!product.isDryRun && isFeatureEnabled('publications')">
        <vl-column>
          <ProductChangeHistoryComponent :run-id="product.runId"></ProductChangeHistoryComponent>
        </vl-column>
      </vl-grid>
    </vl-layout>
  </vl-region>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, onUnmounted, reactive, toRefs } from 'vue';
import { ProductDetailService, ProductDetailServiceInstance } from './ProductDetail.service';
import { useRoute } from 'vue-router';
import PbsEntityRouterLink from '@/components/PbsEntityRouterLink.vue';
import { dateFilter, dateTimeFilter } from '@/filters';
import { useI18n } from 'vue-i18n';
import NotFound from '@/core/security/NotFound.vue';
import ProductCancelForm from './subcomponents/product-cancel-form/ProductCancelForm.vue';
import { useModalStore } from '@/modules/core/store/modal.store';
import { config, isFeatureEnabled } from './../../common/config/config';
import PbsGoBackLink from '@/components/PbsGoBackLink.vue';
import ProductChangeHistoryComponent from '@/modules/products/views/subcomponents/ProductChangeHistoryComponent.vue';
import { RunStep } from '../../api/clients';

export default defineComponent({
  components: {
    NotFound,
    PbsGoBackLink,
    PbsEntityRouterLink,
    ProductChangeHistoryComponent
  },
  setup() {
    const route = useRoute();
    const { t } = useI18n();
    const modalStore = useModalStore();

    const productService: ProductDetailService = ProductDetailServiceInstance;

    // lifecycle hooks
    onMounted(async () => {
      await productService.init(parseInt(route.params.id.toString(), 10));
      if (route.query.created === 'true') {
        alertSuccess(
          t('product.detail.created.alertSuccess.content').toString(),
          t('product.detail.created.alertSuccess.title').toString()
        );
      }
    });
    onUnmounted(() => {
      productService.reset();
    });

    // reactive data
    const data = reactive({
      alert: {
        visible: false,
        isError: false,
        isSuccess: false,
        title: '',
        content: '',
        icon: ''
      }
    });

    const id = computed((): number => {
      return parseInt(route.params.id as string, 10);
    });

    const product = computed(() => productService.state.product);
    const notFound = computed(() => productService.state.notFound);
    const loading = computed(() => productService.state.loading);

    const getMetadataUrl = (uuid: string): string => {
      return `${config.integrations.metadataUrl}/${uuid}`;
    };

    const getMetadataAnnotation = (productStatusId: number): string => {
      if (productStatusId == 2 || productStatusId == 3 || productStatusId == 100) {
        return t('product.detail.metadata.publicationSkipped.label');
      } else {
        return t('product.detail.metadata.publicationNotYetAvailable.label');
      }
    };

    const showcancelProductModal = () => {
      const modalContent = {
        title: t('product.detail.cancelProduct.label'),
        component: ProductCancelForm,
        props: {
          id: id.value
        }
      };

      modalStore.showModal(modalContent);
    };

    const getRunStepDescription = (runStep: RunStep) => {
      let name = runStep.entityName ? `${runStep.name} ${runStep.entityName}` : runStep.name;

      if(runStep.retryCount != null && runStep.retryCount > 0){
        name = `${name}, herhaling ${runStep.retryCount}`
      }

      return name;
    };

    const getFirstErrorLine = (error: string) => {
      return error ? error.split('\n')[0] : '';
    };

    const alertSuccess = (message: string, title: string) => {
      data.alert = {
        visible: true,
        isError: false,
        isSuccess: true,
        icon: 'check',
        title: title,
        content: message
      };
    };

    const closeAlert = () => {
      data.alert = {
        visible: false,
        isError: false,
        isSuccess: false,
        icon: '',
        title: '',
        content: ''
      };
    };

    return {
      ...toRefs(data),
      product,
      productService,
      dateFilter,
      dateTimeFilter,
      notFound,
      loading,
      getMetadataUrl,
      getRunStepDescription,
      getFirstErrorLine,
      showcancelProductModal,
      closeAlert,
      getMetadataAnnotation,
      isFeatureEnabled
    };
  }
});
</script>

<style lang="scss" scoped></style>
